import React, { createContext, useContext, useMemo, useRef } from 'react';
import { StyleSheet, type ViewStyle, type TextStyle, type ImageStyle } from 'react-native';
import { THEMES, DEFAULT_THEME_ID, type Theme, type ThemeId } from './themes';
import { getWallpaper, type Wallpaper, type WallpaperId } from './wallpapers';

interface ThemeContextValue {
  theme: Theme;
  setThemeId: (id: ThemeId) => void;
  /** Resolved wallpaper; `uri` is null when the theme colour should show. */
  wallpaper: Wallpaper;
  setWallpaper: (id: WallpaperId, customUri?: string | null) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: THEMES[DEFAULT_THEME_ID],
  setThemeId: () => {},
  wallpaper: getWallpaper('none'),
  setWallpaper: () => {},
});

export interface ThemeProviderProps {
  themeId?: ThemeId;
  setThemeId?: (id: ThemeId) => void;
  wallpaperId?: WallpaperId;
  /** Uploaded image URL, used when `wallpaperId` is `custom`. */
  customWallpaperUri?: string | null;
  setWallpaper?: (id: WallpaperId, customUri?: string | null) => void;
  children: React.ReactNode;
}

export function ThemeProvider({
  themeId,
  setThemeId,
  wallpaperId,
  customWallpaperUri,
  setWallpaper,
  children,
}: ThemeProviderProps) {
  const value = useMemo<ThemeContextValue>(() => {
    const base = getWallpaper(wallpaperId);
    return {
      theme: THEMES[themeId ?? DEFAULT_THEME_ID] ?? THEMES[DEFAULT_THEME_ID],
      setThemeId: setThemeId ?? (() => {}),
      wallpaper:
        wallpaperId === 'custom' && customWallpaperUri
          ? { id: 'custom', label: 'Your picture', description: '', uri: customWallpaperUri }
          : base,
      setWallpaper: setWallpaper ?? (() => {}),
    };
  }, [themeId, setThemeId, wallpaperId, customWallpaperUri, setWallpaper]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Theme {
  return useContext(ThemeContext).theme;
}

export function useThemeControls(): ThemeContextValue {
  return useContext(ThemeContext);
}

/** The resolved wallpaper for the current theme. */
export function useWallpaper(): Wallpaper {
  return useContext(ThemeContext).wallpaper;
}

/**
 * Mirrors React Native's own StyleSheet.NamedStyles. Without this the factory's
 * return type widens `flexDirection: 'row'` to `string`, which no longer
 * satisfies ViewStyle — the same reason StyleSheet.create is generic.
 */
type NamedStyles<T> = { [P in keyof T]: ViewStyle | TextStyle | ImageStyle };

/**
 * Builds a themed StyleSheet, cached per theme.
 *
 * Styles have to become a function of the theme once themes are switchable,
 * but calling StyleSheet.create on every render would churn the style registry.
 * The factory runs once per theme and the result is reused, so switching is a
 * single re-render and staying put costs nothing.
 *
 *   const useStyles = makeStyles((t) => ({ box: { backgroundColor: t.colors.face } }));
 *   const styles = useStyles();
 */
export function makeStyles<T extends NamedStyles<T> | NamedStyles<any>>(
  factory: (theme: Theme) => T | NamedStyles<T>
) {
  const cache = new Map<ThemeId, T>();
  return function useStyles(): T {
    const theme = useTheme();
    const cached = cache.get(theme.id);
    if (cached) return cached;
    const created = StyleSheet.create(factory(theme)) as T;
    cache.set(theme.id, created);
    return created;
  };
}

/** Storage key for the persisted choice, shared by every platform. */
export const THEME_STORAGE_KEY = 'ogaim.theme';
